import React from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { citiesService } from "../../../shared/services";
import { getService } from "../../../clients/feathers.clients";

const { Text, Title } = Typography;

type ShippingMode = "neighborhood" | "kilometers";

type ShippingRateSettings = {
  id: number;
  store_id: number;
  mode: ShippingMode;
  status: "active" | "inactive";
  fulfillment_company_id: number | null;
  origin_store_id: number | null;
  origin_lat: number | null;
  origin_lng: number | null;
  fallback_price: number | null;
};

type NeighborhoodRate = {
  id: number;
  name: string;
  city_id: number | null;
  price: number;
  status: "active" | "inactive";
};

type DistanceRate = {
  id: number;
  min_km: number;
  max_km: number;
  price: number;
  status: "active" | "inactive";
};

const settingsService = getService("shipping-rate-settings");
const neighborhoodService = getService("shipping-neighborhood-rates");
const distanceService = getService("shipping-distance-rates");

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(value || 0);

type Props = {
  storeId: number;
  compact?: boolean;
};

export const StoreShippingRatesPanel = ({ storeId, compact }: Props) => {
  const [loading, setLoading] = React.useState(false);
  const [settings, setSettings] = React.useState<ShippingRateSettings | null>(
    null,
  );
  const [neighborhoods, setNeighborhoods] = React.useState<NeighborhoodRate[]>(
    [],
  );
  const [distances, setDistances] = React.useState<DistanceRate[]>([]);
  const [cities, setCities] = React.useState<any[]>([]);

  const [neighborhoodModalOpen, setNeighborhoodModalOpen] =
    React.useState(false);
  const [distanceModalOpen, setDistanceModalOpen] = React.useState(false);
  const [editingNeighborhood, setEditingNeighborhood] =
    React.useState<NeighborhoodRate | null>(null);
  const [editingDistance, setEditingDistance] =
    React.useState<DistanceRate | null>(null);

  const [settingsForm] = Form.useForm();
  const [neighborhoodForm] = Form.useForm();
  const [distanceForm] = Form.useForm();

  const loadAll = React.useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const [
        settingsRes,
        neighborhoodsRes,
        distancesRes,
        citiesRes,
      ] = await Promise.all([
        settingsService.find({
          query: { store_id: storeId, $limit: 1, $sort: { id: 1 } },
        }),
        neighborhoodService.find({
          query: {
            store_id: storeId,
            $limit: 200,
            $sort: { position: 1, id: -1 },
          },
        }),
        distanceService.find({
          query: {
            store_id: storeId,
            $limit: 200,
            $sort: { min_km: 1, id: 1 },
          },
        }),
        citiesService.find({
          query: { $limit: 300, $sort: { name: 1 } },
        }),
      ]);

      let settingsData =
        ("data" in settingsRes ? settingsRes.data : settingsRes)?.[0] || null;

      if (!settingsData) {
        settingsData = await settingsService.create({
          store_id: storeId,
          mode: "neighborhood",
          status: "inactive",
          origin_store_id: storeId,
          fallback_price: 0,
        });
      }

      const neighborhoodData =
        "data" in neighborhoodsRes ? neighborhoodsRes.data : neighborhoodsRes;
      const distanceData =
        "data" in distancesRes ? distancesRes.data : distancesRes;
      const cityData = "data" in citiesRes ? citiesRes.data : citiesRes;

      setSettings(settingsData);
      setNeighborhoods(Array.isArray(neighborhoodData) ? neighborhoodData : []);
      setDistances(Array.isArray(distanceData) ? distanceData : []);
      setCities(Array.isArray(cityData) ? cityData : []);

      if (settingsData) {
        settingsForm.setFieldsValue({
          ...settingsData,
          status: settingsData.status === "active",
        });
      }
    } catch (error: any) {
      message.error(error?.message || "No se pudieron cargar las tarifas");
    } finally {
      setLoading(false);
    }
  }, [settingsForm, storeId]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  const saveSettings = async (values: any) => {
    if (!settings?.id) {
      message.error("No hay configuración de tarifas inicializada");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        store_id: storeId,
        mode: values.mode,
        status: values.status ? "active" : "inactive",
        fulfillment_company_id: null,
        origin_store_id: storeId,
        origin_lat: values.origin_lat ?? null,
        origin_lng: values.origin_lng ?? null,
        fallback_price: 0,
      };
      await settingsService.patch(settings.id, payload);
      message.success("Configuración de tarifas guardada");
      await loadAll();
    } catch (error: any) {
      message.error(error?.message || "No se pudo guardar la configuración");
    } finally {
      setLoading(false);
    }
  };

  const openNeighborhoodModal = (record?: NeighborhoodRate) => {
    setEditingNeighborhood(record || null);
    neighborhoodForm.setFieldsValue(
      record || {
        name: "",
        city_id: undefined,
        price: 0,
        status: "active",
      },
    );
    setNeighborhoodModalOpen(true);
  };

  const saveNeighborhood = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        store_id: storeId,
        name: String(values.name || "").trim(),
        city_id: values.city_id || null,
        price: Number(values.price) || 0,
        status: values.status || "active",
      };
      if (editingNeighborhood) {
        await neighborhoodService.patch(editingNeighborhood.id, payload);
        message.success("Barrio actualizado");
      } else {
        await neighborhoodService.create(payload);
        message.success("Barrio creado");
      }
      setNeighborhoodModalOpen(false);
      await loadAll();
    } catch (error: any) {
      message.error(error?.message || "No se pudo guardar el barrio");
    } finally {
      setLoading(false);
    }
  };

  const removeNeighborhood = async (id: number) => {
    try {
      await neighborhoodService.remove(id);
      message.success("Barrio eliminado");
      await loadAll();
    } catch (error: any) {
      message.error(error?.message || "No se pudo eliminar el barrio");
    }
  };

  const openDistanceModal = (record?: DistanceRate) => {
    setEditingDistance(record || null);
    distanceForm.setFieldsValue(
      record || {
        min_km: 0,
        max_km: 3,
        price: 0,
        status: "active",
      },
    );
    setDistanceModalOpen(true);
  };

  const saveDistance = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        store_id: storeId,
        min_km: Number(values.min_km) || 0,
        max_km: Number(values.max_km) || 0,
        price: Number(values.price) || 0,
        status: values.status || "active",
      };
      if (payload.max_km < payload.min_km) {
        message.error("El km máximo debe ser mayor o igual al mínimo");
        return;
      }
      if (editingDistance) {
        await distanceService.patch(editingDistance.id, payload);
        message.success("Rango actualizado");
      } else {
        await distanceService.create(payload);
        message.success("Rango creado");
      }
      setDistanceModalOpen(false);
      await loadAll();
    } catch (error: any) {
      message.error(error?.message || "No se pudo guardar el rango");
    } finally {
      setLoading(false);
    }
  };

  const removeDistance = async (id: number) => {
    try {
      await distanceService.remove(id);
      message.success("Rango eliminado");
      await loadAll();
    } catch (error: any) {
      message.error(error?.message || "No se pudo eliminar el rango");
    }
  };

  const mode = Form.useWatch("mode", settingsForm) || settings?.mode;

  const cityNameById = React.useMemo(() => {
    return Object.fromEntries(cities.map((city) => [city.id, city.name]));
  }, [cities]);

  const cardStyle = compact
    ? { marginBottom: 0, border: "1px solid #f0f0f0" }
    : {
        borderRadius: 12,
        border: "none",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        marginBottom: 16,
      };

  return (
    <div>
      <Card loading={loading && !settings} style={cardStyle} bodyStyle={{ padding: compact ? 16 : 24 }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={compact ? 5 : 4} style={{ margin: 0 }}>
            Tarifas de domicilio
          </Title>
          <Text type="secondary">
            Configura si esta sede cobra por barrio (monto fijo) o por
            kilómetros desde su ubicación.
          </Text>
        </div>

        <Form
          form={settingsForm}
          layout="vertical"
          onFinish={saveSettings}
          initialValues={{
            mode: "neighborhood",
            status: false,
            fallback_price: 0,
          }}
        >
          <Row gutter={[16, 8]}>
            <Col xs={24}>
              <Form.Item
                label="Modo de tarifa"
                name="mode"
                rules={[{ required: true, message: "Selecciona un modo" }]}
              >
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  options={[
                    { label: "Monto fijo por barrio", value: "neighborhood" },
                    { label: "Por kilómetros", value: "kilometers" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Tarifas activas"
                name="status"
                valuePropName="checked"
              >
                <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
              </Form.Item>
            </Col>

            {mode === "kilometers" && (
              <>
                <Col xs={12} md={12}>
                  <Form.Item
                    label="Latitud origen (opcional)"
                    name="origin_lat"
                    extra="Por defecto usa la ubicación de esta sede"
                  >
                    <InputNumber style={{ width: "100%" }} step={0.000001} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={12}>
                  <Form.Item label="Longitud origen (opcional)" name="origin_lng">
                    <InputNumber style={{ width: "100%" }} step={0.000001} />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          <Button type="primary" htmlType="submit" loading={loading}>
            Guardar configuración
          </Button>
        </Form>
      </Card>

      {mode === "neighborhood" ? (
        <Card style={{ ...cardStyle, marginTop: 16 }} bodyStyle={{ padding: compact ? 16 : 24 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div>
              <Title level={compact ? 5 : 4} style={{ margin: 0 }}>
                Barrios y precios fijos
              </Title>
              <Text type="secondary">
                El cliente debe tener el barrio en su dirección.
              </Text>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openNeighborhoodModal()}
            >
              Nuevo barrio
            </Button>
          </div>

          <Table
            rowKey="id"
            loading={loading}
            dataSource={neighborhoods}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            size={compact ? "small" : "middle"}
            columns={[
              {
                title: "Barrio",
                dataIndex: "name",
                render: (name: string) => <strong>{name}</strong>,
              },
              {
                title: "Ciudad",
                dataIndex: "city_id",
                render: (cityId: number | null) =>
                  cityId ? cityNameById[cityId] || `#${cityId}` : "Todas",
              },
              {
                title: "Precio",
                dataIndex: "price",
                render: (price: number) => (
                  <Tag color="green">{formatCurrency(Number(price))}</Tag>
                ),
              },
              {
                title: "Estado",
                dataIndex: "status",
                render: (status: string) => (
                  <Tag color={status === "active" ? "success" : "default"}>
                    {status === "active" ? "Activo" : "Inactivo"}
                  </Tag>
                ),
              },
              {
                title: "Acciones",
                key: "actions",
                render: (_: any, record: NeighborhoodRate) => (
                  <Space>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => openNeighborhoodModal(record)}
                    />
                    <Popconfirm
                      title={`¿Eliminar "${record.name}"?`}
                      onConfirm={() => removeNeighborhood(record.id)}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      ) : (
        <Card style={{ ...cardStyle, marginTop: 16 }} bodyStyle={{ padding: compact ? 16 : 24 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div>
              <Title level={compact ? 5 : 4} style={{ margin: 0 }}>
                Rangos por kilómetros
              </Title>
              <Text type="secondary">
                Distancia en línea recta desde esta sede hasta el cliente.
              </Text>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openDistanceModal()}
            >
              Nuevo rango
            </Button>
          </div>

          <Table
            rowKey="id"
            loading={loading}
            dataSource={distances}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            size={compact ? "small" : "middle"}
            columns={[
              { title: "Desde (km)", dataIndex: "min_km" },
              { title: "Hasta (km)", dataIndex: "max_km" },
              {
                title: "Precio",
                dataIndex: "price",
                render: (price: number) => (
                  <Tag color="blue">{formatCurrency(Number(price))}</Tag>
                ),
              },
              {
                title: "Estado",
                dataIndex: "status",
                render: (status: string) => (
                  <Tag color={status === "active" ? "success" : "default"}>
                    {status === "active" ? "Activo" : "Inactivo"}
                  </Tag>
                ),
              },
              {
                title: "Acciones",
                key: "actions",
                render: (_: any, record: DistanceRate) => (
                  <Space>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => openDistanceModal(record)}
                    />
                    <Popconfirm
                      title="¿Eliminar este rango?"
                      onConfirm={() => removeDistance(record.id)}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      )}

      <Modal
        title={editingNeighborhood ? "Editar barrio" : "Nuevo barrio"}
        open={neighborhoodModalOpen}
        onCancel={() => setNeighborhoodModalOpen(false)}
        onOk={() => neighborhoodForm.submit()}
        okText="Guardar"
        destroyOnClose
      >
        <Form
          form={neighborhoodForm}
          layout="vertical"
          onFinish={saveNeighborhood}
        >
          <Form.Item
            label="Nombre del barrio"
            name="name"
            rules={[{ required: true, message: "Ingresa el barrio" }]}
          >
            <Input placeholder="Ej: Riomar" />
          </Form.Item>
          <Form.Item label="Ciudad (opcional)" name="city_id">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Todas las ciudades"
              options={cities.map((city) => ({
                value: city.id,
                label: city.name,
              }))}
            />
          </Form.Item>
          <Form.Item
            label="Precio fijo"
            name="price"
            rules={[{ required: true, message: "Ingresa el precio" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Estado" name="status" initialValue="active">
            <Select
              options={[
                { value: "active", label: "Activo" },
                { value: "inactive", label: "Inactivo" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingDistance ? "Editar rango" : "Nuevo rango"}
        open={distanceModalOpen}
        onCancel={() => setDistanceModalOpen(false)}
        onOk={() => distanceForm.submit()}
        okText="Guardar"
        destroyOnClose
      >
        <Form form={distanceForm} layout="vertical" onFinish={saveDistance}>
          <Form.Item
            label="Desde (km)"
            name="min_km"
            rules={[{ required: true, message: "Ingresa el mínimo" }]}
          >
            <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Hasta (km)"
            name="max_km"
            rules={[{ required: true, message: "Ingresa el máximo" }]}
          >
            <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Precio"
            name="price"
            rules={[{ required: true, message: "Ingresa el precio" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Estado" name="status" initialValue="active">
            <Select
              options={[
                { value: "active", label: "Activo" },
                { value: "inactive", label: "Inactivo" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
